import { Dispatch, useMemo, useReducer } from 'react';
import { localStorageManager } from '../../../../src/utils/localStorageManager';
import omit from 'lodash/omit'

type FnWithoutFirstArg<Fn> = Fn extends ((first: any, ...rest: infer R) => any) ? (...rest: R) => any : never;

type Reducers<State, R> = { [key in keyof R]: (state: State, action: { payload: any }) => State };

type Action<State, Fn> = Fn extends (state: State, action: infer A) => State
                         ? A extends { payload: infer P } ? (payload: P) => void : () => void
                         : () => void;

type Actions<State, R> = { [key in keyof R]: Action<State, R[key]> };

type SelectorsParam<State, S> = { [key in keyof S]: (state: State, ...args: any[]) => any };
type Selector<Fn> = FnWithoutFirstArg<Fn>;
type Selectors<S> = { [key in keyof S]: Selector<S[key]> };

type EffectsParam<State, E> = { [key in keyof E]: (state: State, actions: any, ...args: any[]) => void };
type Effect<Fn> = FnWithoutFirstArg<FnWithoutFirstArg<Fn>>;
type Effects<E> = { [key in keyof E]: Effect<E[key]> };
type UseCreateStateArgsConfig<State> = {
  persistKey?: string,
  persistStateSelector?: (state: State) => Partial<State>,
  persistSkipFields?: (keyof State)[],
}

type UseCreateStateArgs<State, R, S, E> = {
  initialState: State,
  reducers: R & Reducers<State, R>,
  selectors?: S & SelectorsParam<State, S>,
  sideEffects?: E & EffectsParam<State, E>,
  config?: UseCreateStateArgsConfig<State>,
}

type UseCreateStateResult<State, R, S, E> = {
  state: State,
  actions: Actions<State, R>,
  selectors: Selectors<S>,
  sideEffects: Effects<E>
}

export function useCreateState<State, R, S, E>(props: UseCreateStateArgs<State, R, S, E>): UseCreateStateResult<typeof props.initialState, R, S, E> {
  const statePersistKey = props.config && props.config.persistKey;
  const initialState = getInitialState(props.initialState, statePersistKey);

  const createReducer = (state: any, action: any) => {
    const sliceFn = props.reducers[action.type as keyof R];
    if (!sliceFn) {
      throw new Error('Reduce function undefined');
    }
    return sliceFn(state, action);
  };

  const [state, dispatch] = useReducer(createReducer, initialState);

  if (statePersistKey) {
    persistState(state, statePersistKey, props.config?.persistStateSelector, props.config?.persistSkipFields as any);
  }

  const actions: Actions<typeof props.initialState, R> = buildActions(props.reducers, dispatch);
  const selectors: Selectors<S> = buildSelectors(state, props.selectors || ({} as any));
  const sideEffects: Effects<E> = buildSideEffects(state, props.sideEffects || ({} as any), actions);

  return { state, actions, selectors, sideEffects } as const;
}

function buildActions<State, R>(reducers: Reducers<State, R>, dispatch: Dispatch<any>) {
  return Object.keys(reducers)
    .reduce((actions, name) => {
      actions[name as keyof R] = ((payload: any) => dispatch({ type: name, payload })) as Action<State, R[keyof R]>;
      return actions;
    }, {} as Actions<State, R>) as Actions<State, R>;
}

function buildSelectors<State, S>(state: State, selectors: SelectorsParam<State, S>) {
  return Object.keys(selectors)
    .reduce((acc, selectorName) => {
      const select = selectors[selectorName as keyof S];
      // eslint-disable-next-line react-hooks/exhaustive-deps
      acc[selectorName as keyof S] = ((...args: any[]) => useMemo(() => select(state, ...args), [state, ...args])) as Selector<S[keyof S]>;
      return acc;
    }, {} as Selectors<S>) as Selectors<S>;
}

function buildSideEffects<State, E>(state: State, sideEffects: EffectsParam<State, E>, actions?: any) {
  return Object.keys(sideEffects)
    .reduce((res, name) => {
      const fn = sideEffects[name as keyof E];
      res[name as keyof E] = ((...args: any[]) => fn(state, actions, ...args)) as Effect<E[keyof E]>;
      return res;
    }, {} as Effects<E>);
}

function getInitialState(initialStateFromProps: any, statePersistKey: any) {
  let localStorage: any = undefined;
  let initialState = initialStateFromProps;

  if (statePersistKey) {
    localStorage = localStorageManager(statePersistKey);
    let savedState = localStorage.getItem();

    if (savedState) {
      initialState = savedState;
    }
  }

  return initialState;
}

function persistState(state: any, statePersistKey: any, persistStateSelector?:(state:any)=>any, skipFields?:string[]) {
  if (statePersistKey) {
    const localStorage = localStorageManager(statePersistKey);
    state = persistStateSelector ? persistStateSelector(state) : state;
    let stateToSave = skipFields ? omit(state, skipFields) : state;
    localStorage.setItem(stateToSave);
  }
}
